import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLang } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import SmoothScroll from "@/components/SmoothScroll";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import PromoBanner from "@/components/PromoBanner"; // TEMP-PROMO: homepage promotion banner (see src/lib/promoConfig.js)
import ProtectedRoute from "@/components/portal/ProtectedRoute";

// Below-the-fold homepage sections are code-split so they stay out of the main bundle
// and never delay the hero paint / inflate mobile TBT.
const FeatureStrip = lazy(() => import("@/components/FeatureStrip"));
const OttawaProducts = lazy(() => import("@/components/OttawaProducts"));
const ServiceArea = lazy(() => import("@/components/ServiceArea"));
const TwoPaths = lazy(() => import("@/components/TwoPaths"));
const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const HomeFaq = lazy(() => import("@/components/HomeFaq"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const OrderForm = lazy(() => import("@/components/OrderForm"));
const Footer = lazy(() => import("@/components/Footer"));

// Route components are code-split so the homepage bundle stays lean.
const PaymentResult = lazy(() => import("@/components/PaymentResult"));
const Login = lazy(() => import("@/components/portal/Login"));
const Signup = lazy(() => import("@/components/portal/Signup"));
const ForgotPassword = lazy(() => import("@/components/portal/ForgotPassword"));
const ResetPassword = lazy(() => import("@/components/portal/ResetPassword"));
const VerifyEmail = lazy(() => import("@/components/portal/VerifyEmail"));
const AuthCallback = lazy(() => import("@/components/portal/AuthCallback"));
const Portal = lazy(() => import("@/components/portal/Portal"));
const SupplierPortal = lazy(() => import("@/components/portal/SupplierPortal"));
const LegalPage = lazy(() => import("@/components/LegalPage"));
const WaterDeliveryOttawa = lazy(() => import("@/components/WaterDeliveryOttawa"));
const OfficeWaterDeliveryOttawa = lazy(() => import("@/components/OfficeWaterDeliveryOttawa"));
const HomeWaterDeliveryOttawa = lazy(() => import("@/components/HomeWaterDeliveryOttawa"));
const DistilledWaterDeliveryOttawa = lazy(() => import("@/components/DistilledWaterDeliveryOttawa"));
const FrLivraisonEauOttawa = lazy(() => import("@/components/FrLivraisonEauOttawa"));
const BlogIndex = lazy(() => import("@/components/BlogIndex"));
const BlogArticle = lazy(() => import("@/components/BlogArticle"));
// TEMP-PROMO: dedicated promotion page (see src/lib/promoConfig.js)
const OttawaWaterDeliveryPromotion = lazy(() => import("@/components/OttawaWaterDeliveryPromotion"));
const OttawaBusinessPartners = lazy(() => import("@/components/OttawaBusinessPartners"));
const Faq = lazy(() => import("@/components/Faq"));
const AreaPage = lazy(() => import("@/components/AreaPage"));
const ServicePage = lazy(() => import("@/components/ServicePage"));

// Homepage <title> + meta description per language, kept within Bing limits
// (title <= 60 chars, description 120-155 chars). EN mirrors the static tags in index.html.
const HOME_META = {
  en: {
    title: "Distilled Water Delivery in Ottawa | Capital Flow",
    description:
      "Capital Flow delivers premium distilled water to homes and businesses across Ottawa and the National Capital Region, with reliable scheduled delivery.",
  },
  fr: {
    title: "Livraison d'eau distillée à Ottawa | Capital Flow",
    description:
      "Capital Flow livre de l'eau distillée de qualité supérieure aux résidences et entreprises d'Ottawa et de la région de la capitale nationale.",
  },
};

function Landing() {
  const location = useLocation();
  const { lang } = useLang();
  // Support hash links from other routes (e.g. /#order) landing on the right section.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const scroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.__lenis?.scrollTo) window.__lenis.scrollTo(el, { offset: -20 });
      else el.scrollIntoView({ behavior: "smooth" });
    };
    const timer = setTimeout(scroll, 300);
    return () => clearTimeout(timer);
  }, [location.hash]);

  // Homepage title + meta description (per language, within Bing limits) + canonical + Open Graph +
  // hreflang, UPSERTED in place (find existing tag, mutate it; create only if absent) so there is
  // never a second copy. Same pattern as Seo.jsx — kept OUT of react-helmet because React 19 hoists
  // Helmet's copies on hydration without deduping them against any baked tag, which Bing's renderer
  // can snapshot as duplicates. og:title/description mirror the page title/description. hreflang
  // en/fr/x-default all point to the EN homepage (one bilingual URL).
  useEffect(() => {
    const head = document.head;
    const HOME = "https://capitalflow.ca/";
    const LOGO = "https://capitalflow.ca/icon-512.png";
    const meta = HOME_META[lang] || HOME_META.en;
    const upsertMeta = (attr, key, content) => {
      if (!content) return;
      let el = head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); head.appendChild(el); }
      if (el.getAttribute("content") !== content) el.setAttribute("content", content);
      el.removeAttribute("data-baked");
    };
    const upsertLink = (rel, href, hreflang) => {
      const sel = hreflang ? `link[rel="alternate"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
      let el = head.querySelector(sel);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); if (hreflang) el.setAttribute("hreflang", hreflang); head.appendChild(el); }
      if (el.getAttribute("href") !== href) el.setAttribute("href", href);
      el.removeAttribute("data-baked");
    };
    document.title = meta.title;
    upsertMeta("name", "description", meta.description);
    upsertLink("canonical", HOME);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", meta.title);
    upsertMeta("property", "og:description", meta.description);
    upsertMeta("property", "og:url", HOME);
    upsertMeta("property", "og:image", LOGO);
    [["en", HOME], ["fr", HOME], ["x-default", HOME]].forEach(([hl, href]) => upsertLink("alternate", href, hl));
  }, [lang]);

  return (
    <SmoothScroll>
      <div className="App relative bg-abyss text-foreground">
        <div className="noise-overlay" aria-hidden />
        <PromoBanner />
        <Header />
        <main>
          <Hero />
          <Suspense fallback={null}>
            <FeatureStrip />
            <OttawaProducts />
            <TwoPaths />
            <ServicesSection />
            <HowItWorks />
            <ServiceArea />
            <HomeFaq />
            <Testimonials />
            <OrderForm />
          </Suspense>
        </main>
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </div>
    </SmoothScroll>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Emergent Google OAuth returns to {origin}/portal#session_id=... — handle first.
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={null}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/payment/success" element={<PaymentResult />} />
        <Route path="/payment/cancel" element={<PaymentResult />} />
        <Route path="/login" element={<Login />} />
        <Route path="/supplier" element={<SupplierPortal />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/privacy-policy" element={<LegalPage doc="privacy" />} />
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/water-delivery-ottawa" element={<WaterDeliveryOttawa />} />
        <Route path="/office-water-delivery-ottawa" element={<OfficeWaterDeliveryOttawa />} />
        <Route path="/home-water-delivery-ottawa" element={<HomeWaterDeliveryOttawa />} />
        <Route path="/distilled-water-delivery-ottawa" element={<DistilledWaterDeliveryOttawa />} />
        <Route path="/recurring-water-delivery-ottawa" element={<ServicePage serviceKey="recurring" />} />
        <Route path="/construction-water-delivery-ottawa" element={<ServicePage serviceKey="construction" />} />
        <Route path="/fr/livraison-eau-bureau-ottawa" element={<OfficeWaterDeliveryOttawa forceLang="fr" />} />
        <Route path="/fr/livraison-eau-residentielle-ottawa" element={<HomeWaterDeliveryOttawa forceLang="fr" />} />
        <Route path="/fr/eau-distillee-ottawa" element={<DistilledWaterDeliveryOttawa forceLang="fr" />} />
        <Route path="/fr/livraison-eau-recurrente-ottawa" element={<ServicePage serviceKey="recurring" forceLang="fr" />} />
        <Route path="/fr/livraison-eau-chantier-ottawa" element={<ServicePage serviceKey="construction" forceLang="fr" />} />
        <Route path="/fr/livraison-eau-ottawa" element={<FrLivraisonEauOttawa />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/ottawa-water-delivery-promotion" element={<OttawaWaterDeliveryPromotion />} />
        <Route path="/ottawa-business-partners" element={<OttawaBusinessPartners />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/fr/faq" element={<Faq forceLang="fr" />} />
        <Route path="/ottawa" element={<AreaPage area="ottawa" />} />
        <Route path="/kanata" element={<AreaPage area="kanata" />} />
        <Route path="/orleans" element={<AreaPage area="orleans" />} />
        <Route path="/barrhaven" element={<AreaPage area="barrhaven" />} />
        <Route path="/nepean" element={<AreaPage area="nepean" />} />
        <Route path="/gloucester" element={<AreaPage area="gloucester" />} />
        <Route path="/fr/ottawa" element={<AreaPage area="ottawa" forceLang="fr" />} />
        <Route path="/fr/kanata" element={<AreaPage area="kanata" forceLang="fr" />} />
        <Route path="/fr/orleans" element={<AreaPage area="orleans" forceLang="fr" />} />
        <Route path="/fr/barrhaven" element={<AreaPage area="barrhaven" forceLang="fr" />} />
        <Route path="/fr/nepean" element={<AreaPage area="nepean" forceLang="fr" />} />
        <Route path="/fr/gloucester" element={<AreaPage area="gloucester" forceLang="fr" />} />
        <Route path="/fr/ottawa-business-partners" element={<OttawaBusinessPartners forceLang="fr" />} />
        <Route path="/portal" element={<ProtectedRoute><Portal /></ProtectedRoute>} />
        <Route path="*" element={<Landing />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="bottom-right" theme="dark" richColors />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
